import React from 'react';
import { SchemaField, BlockSchemaField } from './useContractWizard';
import { canEditBlock } from './useContractWizard';
import css from './ContractWizardModal.module.css';

export function renderField(
  field: SchemaField,
  isRequired: boolean,
  formValues: Record<string, any>,
  validationErrors: Record<string, string>,
  handleFieldChange: (key: string, value: any) => void,
  selectedEmploymentType: string,
  collapsedBlocks: Record<string, boolean>,
  setCollapsedBlocks: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  patchProfile: boolean
): React.ReactNode {
  const value = formValues[field.key] ?? '';
  const hasError = !!validationErrors[field.key];
  const hasDefaultValue = field.default_value !== undefined && field.default_value !== null;
  const isModified = hasDefaultValue && value !== field.default_value;
  const isMinijobAutoField = selectedEmploymentType === 'MINIJOB' && field.key === 'monthly_hours';

  const handleResetToDefault = () => {
    if (hasDefaultValue) handleFieldChange(field.key, field.default_value);
  };

  const hasOtherEmploymentValue = formValues['has_other_employment'];
  const hasOtherEmployment = hasOtherEmploymentValue === 'true' || hasOtherEmploymentValue === true;
  const isConditionalEmploymentField = [
    'other_employment_employer',
    'other_employment_start_date',
    'other_employment_monthly_salary',
  ].includes(field.key);
  if (isConditionalEmploymentField && !hasOtherEmployment) return null;

  const isFixedTermValue = formValues['is_fixed_term'];
  const isFixedTerm = isFixedTermValue === 'true' || isFixedTermValue === true;
  const isConditionalFixedTermField = ['contract_end_date', 'fixed_term_reason'].includes(field.key);
  if (isConditionalFixedTermField && !isFixedTerm) return null;

  if (field.type === 'text') {
    return renderTextBlock(field, isRequired, formValues, validationErrors, handleFieldChange, collapsedBlocks, setCollapsedBlocks, patchProfile);
  }

  const borderError = hasError ? { border: '1px solid #ef4444' } : { border: '1px solid #e5e7eb' };

  return (
    <div key={field.key} className={css.fieldContainer}>
      <div className={css.fieldLabelRow}>
        <label className={css.fieldLabelNoMargin}>
          {field.label} {isRequired && '*'}
          {isMinijobAutoField && <span className={css.autoTag}>{'🔄 Auto'}</span>}
        </label>
        {isModified && !isMinijobAutoField && (
          <button type="button" onClick={handleResetToDefault} className={css.resetDefaultBtn}>
            Reset to Default
          </button>
        )}
      </div>
      {field.type === 'string' && (
        <input type="text" value={value} onChange={(e) => handleFieldChange(field.key, e.target.value)} className={css.inputStyle} style={borderError} />
      )}
      {field.type === 'number' && (
        <input
          type="number" value={value}
          onChange={(e) => handleFieldChange(field.key, e.target.value ? Number(e.target.value) : '')}
          disabled={isMinijobAutoField} min={field.constraints?.min} max={field.constraints?.max}
          className={css.inputStyle}
          style={{ ...borderError, backgroundColor: isMinijobAutoField ? '#f9fafb' : '#ffffff', cursor: isMinijobAutoField ? 'not-allowed' : 'text' }}
        />
      )}
      {field.type === 'decimal' && (
        <input
          type="number" step="0.01" value={value}
          onChange={(e) => handleFieldChange(field.key, e.target.value ? parseFloat(e.target.value) : '')}
          disabled={isMinijobAutoField} min={field.constraints?.min} max={field.constraints?.max}
          className={css.inputStyle}
          style={{ ...borderError, backgroundColor: isMinijobAutoField ? '#f9fafb' : '#ffffff', cursor: isMinijobAutoField ? 'not-allowed' : 'text' }}
        />
      )}
      {field.type === 'date' && (
        <input type="date" value={value} onChange={(e) => handleFieldChange(field.key, e.target.value)} className={css.inputStyle} style={borderError} />
      )}
      {field.type === 'select' && (
        <select value={value} onChange={(e) => handleFieldChange(field.key, e.target.value)} className={css.inputStyle} style={borderError}>
          <option value="">Select...</option>
          {field.options?.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
        </select>
      )}
      {field.type === 'checkbox' && (
        <label className={css.checkboxFieldLabel}>
          <input type="checkbox" checked={value === true || value === 'true'} onChange={(e) => handleFieldChange(field.key, e.target.checked)} className={css.checkboxInput} />
          <span className={css.checkboxText}>{field.help_text || 'Aktivieren'}</span>
        </label>
      )}
      {field.profile_map && patchProfile && (
        <div className={css.profileMapHint}><span>{'ℹ️'}</span><span>Will update employee profile: {field.profile_map}</span></div>
      )}
      {field.help_text && <div className={css.helpText}>{field.help_text}</div>}
      {hasError && <div className={css.fieldError}>{validationErrors[field.key]}</div>}
    </div>
  );
}

function renderTextBlock(
  field: SchemaField, isRequired: boolean,
  formValues: Record<string, any>, validationErrors: Record<string, string>,
  handleFieldChange: (key: string, value: any) => void,
  collapsedBlocks: Record<string, boolean>,
  setCollapsedBlocks: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  patchProfile: boolean
): React.ReactNode {
  const value = formValues[field.key] ?? '';
  const hasError = !!validationErrors[field.key];
  const hasDefaultValue = field.default_value !== undefined && field.default_value !== null;
  const isModified = hasDefaultValue && value !== field.default_value;
  const isCollapsed = collapsedBlocks[field.key] ?? true;

  const handleResetToDefault = () => {
    if (hasDefaultValue) handleFieldChange(field.key, field.default_value);
  };

  const toggleCollapse = () => {
    setCollapsedBlocks((prev) => ({ ...prev, [field.key]: !prev[field.key] }));
  };

  return (
    <div key={field.key} className={css.textBlock}>
      <div className={css.textBlockHeader} onClick={toggleCollapse}>
        <div className={css.textBlockHeaderLeft}>
          <span className={css.textBlockArrow}>{isCollapsed ? '\u25B6' : '\u25BC'}</span>
          <span className={css.textBlockTitle}>{field.label} {isRequired && '*'}</span>
        </div>
        {isModified && (
          <button type="button" onClick={(e) => { e.stopPropagation(); handleResetToDefault(); }} className={css.resetDefaultBtn}>
            Reset to Default
          </button>
        )}
      </div>
      {!isCollapsed && (
        <div className={css.textBlockBody}>
          <textarea
            value={value} onChange={(e) => handleFieldChange(field.key, e.target.value)} rows={8}
            className={css.textBlockTextarea}
            style={hasError ? { border: '1px solid #ef4444' } : { border: '1px solid #e5e7eb' }}
          />
          {field.profile_map && patchProfile && (
            <div className={css.textBlockProfileHint}><span>{'ℹ️'}</span><span>Will update employee profile: {field.profile_map}</span></div>
          )}
          {field.help_text && <div className={css.textBlockHelp}>{field.help_text}</div>}
          {hasError && <div className={css.textBlockError}>{validationErrors[field.key]}</div>}
        </div>
      )}
    </div>
  );
}

export function renderBlock(
  block: BlockSchemaField, blockValues: Record<string, string>,
  handleBlockChange: (blockKey: string, value: string) => void,
  isAdmin: boolean, isManager: boolean
): React.ReactNode {
  const value = blockValues[block.block_key] ?? '';
  const canEdit = canEditBlock(block.editable_by, isAdmin, isManager);
  const currentLength = value.length;
  const isOverLimit = block.max_length ? currentLength > block.max_length : false;

  return (
    <div key={block.block_key} className={css.editableBlock}>
      <div className={css.editableBlockHeader}>
        <div className={css.editableBlockHeaderLeft}>
          <span className={css.editableBlockTitle}>{block.label}</span>
          <span className={canEdit ? css.editableBlockBadgeEditable : css.editableBlockBadgeReadonly}>
            {canEdit ? '✏️ Editable' : '🔒 Read-only'}
          </span>
        </div>
        {block.max_length && (
          <div className={isOverLimit ? css.editableBlockCharCountOver : css.editableBlockCharCountNormal}>
            {currentLength} / {block.max_length}
          </div>
        )}
      </div>
      <div className={css.editableBlockBody}>
        <textarea
          value={value} onChange={(e) => handleBlockChange(block.block_key, e.target.value)}
          disabled={!canEdit} rows={8} className={css.editableBlockTextarea}
          style={{ border: isOverLimit ? '1px solid #ef4444' : '1px solid #e5e7eb', backgroundColor: canEdit ? '#ffffff' : '#f9fafb', cursor: canEdit ? 'text' : 'not-allowed' }}
        />
        {block.help_text && <div className={css.editableBlockHelp}>{block.help_text}</div>}
        {isOverLimit && <div className={css.editableBlockOverLimit}>Content exceeds maximum length of {block.max_length} characters</div>}
      </div>
    </div>
  );
}
